import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface LOBreakdown {
  [code: string]: {
    correct: number;
    total: number;
    description?: string;
  };
}

interface QuestionResult {
  question_id: string;
  prompt: string;
  given_answer: string;
  correct_answer: string;
  correct: boolean;
  learning_objective: string;
}

interface GateResultsProps {
  passed: boolean;
  score: number;
  total: number;
  percentage: number;
  loBreakdown: LOBreakdown;
  weakAreas: string[];
  unit: number;
  studentName?: string;
  questionResults?: QuestionResult[];
}

export const GateResults: React.FC<GateResultsProps> = ({
  passed,
  score,
  total,
  percentage,
  loBreakdown,
  weakAreas,
  unit,
  studentName,
  questionResults = [],
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const incorrectQuestions = questionResults.filter(q => !q.correct);

  return (
    <div className={`gate-results ${passed ? 'passed' : 'failed'}`}>
      <div className="result-header">
        {studentName && <p className="student-greeting">Results for {studentName}</p>}
        <div className="result-icon">
          {passed ? '🏆' : '💪'}
        </div>
        <h1>{passed ? 'Congratulations!' : 'Keep Going!'}</h1>
        <p className="result-message">
          {passed
            ? `You've mastered Unit ${unit}!`
            : 'You\'re getting closer. Review the areas below.'}
        </p>
      </div>

      <div className="score-display">
        <div className="score-circle">
          <span className="score-value">{score}</span>
          <span className="score-total">/{total}</span>
        </div>
        <p className="score-percentage">{percentage}%</p>
      </div>

      <div className="lo-breakdown">
        <h3>Performance by Topic</h3>
        {Object.entries(loBreakdown).map(([code, data]) => {
          const pct = data.total > 0 ? (data.correct / data.total) * 100 : 0;
          const isWeak = weakAreas.includes(code);
          const isPerfect = data.correct === data.total;

          return (
            <div key={code} className={`lo-row ${isWeak ? 'weak' : ''} ${isPerfect ? 'perfect' : ''}`}>
              <div className="lo-info">
                <span className="lo-code">{code}</span>
                {data.description && (
                  <span className="lo-description">{data.description}</span>
                )}
              </div>
              <div className="lo-bar">
                <div
                  className="lo-fill"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: isPerfect ? 'var(--success)' : isWeak ? 'var(--error)' : 'var(--primary)'
                  }}
                />
              </div>
              <span className="lo-score">
                {isPerfect ? '✓ ' : ''}{data.correct}/{data.total}
              </span>
            </div>
          );
        })}
      </div>

      {/* Question breakdown for incorrect answers */}
      {incorrectQuestions.length > 0 && (
        <div className="question-breakdown">
          <button
            className="toggle-details-btn"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? '▼' : '▶'} {incorrectQuestions.length} Question{incorrectQuestions.length !== 1 ? 's' : ''} to Review
          </button>

          {showDetails && (
            <div className="incorrect-questions">
              {incorrectQuestions.map((q, idx) => (
                <div key={q.question_id} className="incorrect-question">
                  <div className="question-header-row">
                    <span className="question-num">Q{idx + 1}</span>
                    <span className="question-lo">{q.learning_objective}</span>
                  </div>
                  <p className="question-prompt">{q.prompt}</p>
                  <div className="answer-comparison">
                    <div className="your-answer">
                      <span className="answer-label">Your answer:</span>
                      <span className="answer-value wrong">{q.given_answer || '(no answer)'}</span>
                    </div>
                    <div className="correct-answer">
                      <span className="answer-label">Correct answer:</span>
                      <span className="answer-value correct">{q.correct_answer}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="result-actions">
        {passed ? (
          <Link to={`/practice/${unit + 1}/1`} className="action-button primary">
            Start Unit {unit + 1}
          </Link>
        ) : (
          <>
            <Link to={`/practice?mode=remediation`} className="action-button secondary">
              Practice Weak Areas
            </Link>
            <Link to={`/mastery-gate/${unit}`} className="action-button primary">
              Try Again
            </Link>
          </>
        )}
        <Link to="/" className="action-button">
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
};
